import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../../components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  FormFeedback,
  Input,
  Form,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import toast from 'react-hot-toast';
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import Spinners from "components/Common/Spinner";
import { getAllEventCategory, createCategory, deleteCategory } from "api";

const Categories = () => {
  document.title = "Categories | EventoX";

  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [modal, setModal] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      categoryName: "",
    },
    validationSchema: Yup.object({
      categoryName: Yup.string().required("Please Enter Category Name"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await createCategory({
          categoryName: values.categoryName,
          description: "Description for " + values.categoryName
        });
        if (response && response.success !== false) {
          toast.success("Category Created Successfully");
          fetchCategories();
          toggle();
          validation.resetForm();
        } else {
          toast.error(response.error || "Failed to create category");
        }
      } catch (error) {
        console.error("Create category error:", error);
        toast.error("Error creating category");
      } finally {
        setLoading(false);
      }
    }
  });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await getAllEventCategory();
      if (response && response.success === true) {
        setCategories(response.data || []);
      } else if (Array.isArray(response)) {
        setCategories(response);
      } else if (response?.data) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const toggle = () => {
    setModal(!modal);
  };

  const onClickDelete = (id) => {
    setSelectedCategoryId(id);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedCategoryId) return;
    try {
      const response = await deleteCategory(selectedCategoryId);
      if (!response || response.success !== false) {
        toast.success("Category deleted successfully");
        fetchCategories();
      } else {
        toast.error(response.error || "Failed to delete category");
      }
    } catch (error) {
      toast.error("Error deleting category");
    } finally {
      setDeleteModal(false);
      setSelectedCategoryId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "#",
        cell: (cell) => cell.row.index + 1,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Name',
        accessorKey: 'categoryName',
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell) => (
          <h5 className='font-size-14 mb-1'>
            <Link to='#' className='text-dark'>{cell.getValue()}</Link>
          </h5>
        )
      },
      {
        header: 'Action',
        cell: (cellProps) => {
          const data = cellProps.row.original;
          return (
            <div className="d-flex gap-3">
              <Link
                to="#"
                className="text-danger"
                onClick={() => onClickDelete(data.categoryId)}
              >
                <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
              </Link>
            </div>
          );
        }
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setDeleteModal(false);
          setSelectedCategoryId(null);
        }}
      />
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Category List" />
          <Row>
            {isLoading && categories.length === 0 ? (
              <Spinners setLoading={setLoading} />
            ) : (
              <Col lg="12">
                <Card>
                  <CardBody>
                    <TableContainer
                      columns={columns}
                      data={categories}
                      isGlobalFilter={true}
                      isPagination={true}
                      SearchPlaceholder="Search..."
                      isCustomPageSize={true}
                      isAddButton={true}
                      handleUserClick={toggle}
                      buttonClass="btn btn-success btn-rounded waves-effect waves-light addContact-modal mb-2"
                      buttonName="New Category"
                      tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination"
                    />
                  </CardBody>
                </Card>
              </Col>
            )}

            <Modal isOpen={modal} toggle={toggle}>
              <ModalHeader toggle={toggle} tag="h4">Add Category</ModalHeader>
              <ModalBody>
                <Form onSubmit={e => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}>
                  <Row>
                    <Col xs={12}>
                      <div className="mb-3">
                        <Label className="form-label">Category Name</Label>
                        <Input
                          name="categoryName"
                          type="text"
                          placeholder="Category Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.categoryName || ""}
                          invalid={validation.touched.categoryName && validation.errors.categoryName ? true : false}
                        />
                        {validation.touched.categoryName && validation.errors.categoryName ? (
                          <FormFeedback type="invalid">
                            {validation.errors.categoryName}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="text-end">
                        <button
                          type="submit"
                          className="btn btn-success save-user"
                          disabled={isLoading}
                        >
                          {isLoading ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </ModalBody>
            </Modal>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(Categories);